import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Form, Modal, Table } from 'react-bootstrap';
import pharmacyService from '../../../services/pharmacyService';
import { daysAgoYmd, todayYmd } from '../../../utils/appTimezone';
import { usePharmacy } from '../pharmacyContext';
import { PHARMACY_COPY } from '../pharmacyCopy';
import Icon from '../../common/Icons';
import {
  PharmacyPageHeader,
  LoadingBlock,
  formatDate,
  formatDateTime,
  formatNumber
} from '../ui';

const copy = PHARMACY_COPY.MOVEMENTS;
const PAGE_SIZES = [25, 50, 100];
const DEFAULT_PAGE_SIZE = 25;

const TYPE_KEYS = ['', 'IN', 'OUT', 'TRANSFER_IN', 'TRANSFER_OUT', 'ADJUSTMENT', 'DISPENSE'];

const REF_KEYS = [
  '',
  'ORDER',
  'DISTRIBUTION',
  'SUPPLY_REQUEST',
  'DISPENSATION',
  'ADJUSTMENT',
  'INITIAL',
  'INVENTORY',
  'DUTY_CABINET'
];

const PERIOD_PRESETS = [
  { id: 'today', days: 0 },
  { id: '7d', days: 7 },
  { id: '30d', days: 30 },
  { id: '90d', days: 90 },
  { id: 'all', days: null }
];

function typeLabel(type) {
  if (!type) return copy.FILTER_ALL;
  return copy[`TYPE_${type}`] || type;
}

function refLabel(ref, movement = null) {
  if (ref === '' || ref == null) {
    if (movement && /armoire de garde/i.test(String(movement.reason || ''))) {
      return copy.REF_DUTY_CABINET;
    }
    // Filtre « toutes » vs cellule vide
    return movement ? '—' : copy.ADV_REF_ALL;
  }
  if (ref === 'DUTY_CABINET') return copy.REF_DUTY_CABINET;
  return copy[`REF_${ref}`] || ref;
}

function typePillClass(type) {
  if (type === 'IN' || type === 'TRANSFER_IN') return 'cp-status-pill--ok';
  if (type === 'OUT' || type === 'TRANSFER_OUT' || type === 'DISPENSE') return 'cp-status-pill--warn';
  if (type === 'ADJUSTMENT') return 'cp-status-pill--info';
  return 'cp-status-pill--muted';
}

function signedQuantity(m) {
  const q = Math.abs(Number(m.quantity) || 0);
  if (m.movement_type === 'IN' || m.movement_type === 'TRANSFER_IN') return q;
  if (m.movement_type === 'OUT' || m.movement_type === 'TRANSFER_OUT' || m.movement_type === 'DISPENSE') {
    return -q;
  }
  if (m.movement_type === 'ADJUSTMENT' && m.quantity_before != null && m.quantity_after != null) {
    return Number(m.quantity_after) - Number(m.quantity_before);
  }
  return q;
}

function actorName(m) {
  const name = `${m.actor_first_name || ''} ${m.actor_last_name || ''}`.trim();
  if (name) return name;
  // Mouvement technique / migration sans utilisateur
  if (m.performed_by == null && /correction/i.test(String(m.reason || ''))) {
    return 'Système';
  }
  return '—';
}

function emptyAdv() {
  return {
    date_from: daysAgoYmd(30),
    date_to: todayYmd(),
    site_id: '',
    reference_type: '',
    medication_id: ''
  };
}

export default function PharmacyMovementsPage() {
  const { workSites, workSiteId, toast, mode } = usePharmacy();
  const siteOnly = mode === 'site';
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState({ by_type: {}, qty_in: 0, qty_out: 0 });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const [typeFilter, setTypeFilter] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  const [advOpen, setAdvOpen] = useState(false);
  const [advDraft, setAdvDraft] = useState(emptyAdv);
  const [advApplied, setAdvApplied] = useState(emptyAdv);

  const [medications, setMedications] = useState([]);
  const [medQuery, setMedQuery] = useState('');
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => {
      const next = searchInput.trim();
      setSearch((prev) => {
        if (prev === next) return prev;
        setPage(1);
        return next;
      });
    }, 280);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    pharmacyService.listMedications({ status: 'ACTIVE' }).then((r) => {
      setMedications(r.success ? r.data || [] : []);
    });
  }, []);

  useEffect(() => {
    if (!siteOnly || !workSiteId) return;
    setAdvApplied((prev) =>
      String(prev.site_id) === String(workSiteId) ? prev : { ...prev, site_id: String(workSiteId) }
    );
    setAdvDraft((prev) =>
      String(prev.site_id) === String(workSiteId) ? prev : { ...prev, site_id: String(workSiteId) }
    );
  }, [siteOnly, workSiteId]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (siteOnly && !workSiteId) {
        setRows([]);
        setTotal(0);
        setSummary({ by_type: {}, qty_in: 0, qty_out: 0 });
        return;
      }
      const res = await pharmacyService.listMovements({
        movement_type: typeFilter || undefined,
        search: search || undefined,
        site_id: siteOnly ? workSiteId : advApplied.site_id || undefined,
        reference_type: advApplied.reference_type || undefined,
        medication_id: advApplied.medication_id || undefined,
        date_from: advApplied.date_from || undefined,
        date_to: advApplied.date_to || undefined,
        limit: pageSize,
        offset: (page - 1) * pageSize
      });
      if (res.success) {
        setRows(res.data || []);
        setTotal(Number(res.total || 0));
        setSummary(res.summary || { by_type: {}, qty_in: 0, qty_out: 0 });
      } else {
        setRows([]);
        setTotal(0);
        toast?.('danger', res.message || 'Impossible de charger les mouvements.');
      }
    } catch (e) {
      setRows([]);
      setTotal(0);
      toast?.('danger', e.message || 'Impossible de charger les mouvements.');
    } finally {
      setLoading(false);
    }
  }, [typeFilter, search, advApplied, page, pageSize, toast, siteOnly, workSiteId]);

  useEffect(() => {
    load();
  }, [load]);

  const byType = summary.by_type || {};
  const typeCount = (t) => Number(byType[t]?.count || 0);

  const stats = useMemo(() => {
    const transfer =
      Number(byType.TRANSFER_IN?.count || 0) + Number(byType.TRANSFER_OUT?.count || 0);
    const all =
      Number(byType.IN?.count || 0) +
      Number(byType.OUT?.count || 0) +
      transfer +
      Number(byType.ADJUSTMENT?.count || 0) +
      Number(byType.DISPENSE?.count || 0);
    return {
      total: all,
      in: Number(byType.IN?.count || 0),
      out: Number(byType.OUT?.count || 0),
      transfer,
      dispense: Number(byType.DISPENSE?.count || 0),
      adjust: Number(byType.ADJUSTMENT?.count || 0),
      qtyIn: Number(summary.qty_in || 0),
      qtyOut: Number(summary.qty_out || 0)
    };
  }, [summary]);

  const setTypeFilterSafe = (next) => {
    setPage(1);
    setTypeFilter(typeof next === 'function' ? next(typeFilter) : next);
  };

  const applyAdvanced = () => {
    setPage(1);
    const next = { ...advDraft };
    if (siteOnly && workSiteId) next.site_id = String(workSiteId);
    setAdvApplied(next);
    setAdvOpen(false);
  };

  const resetAdvanced = () => {
    const next = emptyAdv();
    if (siteOnly && workSiteId) next.site_id = String(workSiteId);
    setPage(1);
    setAdvDraft(next);
    setAdvApplied(next);
    setMedQuery('');
  };

  const changePageSize = (size) => {
    setPage(1);
    setPageSize(size);
  };

  const pageCount = Math.max(1, Math.ceil(total / pageSize) || 1);
  const rangeFrom = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const rangeTo = Math.min(page * pageSize, total);

  const pageItems = useMemo(() => {
    if (pageCount <= 7) {
      return Array.from({ length: pageCount }, (_, i) => i + 1);
    }
    const items = new Set([1, pageCount, page, page - 1, page + 1]);
    return [...items].filter((n) => n >= 1 && n <= pageCount).sort((a, b) => a - b);
  }, [page, pageCount]);

  useEffect(() => {
    if (page > pageCount) setPage(pageCount);
  }, [page, pageCount]);

  const activeAdvCount = useMemo(() => {
    let n = 0;
    if (!siteOnly && advApplied.site_id) n += 1;
    if (advApplied.reference_type) n += 1;
    if (advApplied.medication_id) n += 1;
    const def = emptyAdv();
    if (advApplied.date_from !== def.date_from || advApplied.date_to !== def.date_to) n += 1;
    return n;
  }, [advApplied, siteOnly]);

  const selectedMedName = useMemo(() => {
    if (!advApplied.medication_id) return '';
    return medications.find((m) => String(m.id) === String(advApplied.medication_id))?.name || '';
  }, [advApplied.medication_id, medications]);

  const medSuggestions = useMemo(() => {
    const q = medQuery.trim().toLowerCase();
    if (!q || q.length < 1) return [];
    return medications
      .filter((m) => String(m.name || '').toLowerCase().includes(q))
      .slice(0, 8);
  }, [medications, medQuery]);

  const periodPresetId = useMemo(() => {
    const today = todayYmd();
    for (const p of PERIOD_PRESETS) {
      if (p.days === null) {
        if (!advDraft.date_from && !advDraft.date_to) return 'all';
        continue;
      }
      if (p.days === 0) {
        if (advDraft.date_from === today && advDraft.date_to === today) return 'today';
        continue;
      }
      if (advDraft.date_from === daysAgoYmd(p.days) && advDraft.date_to === today) return p.id;
    }
    return null;
  }, [advDraft]);

  const applyPreset = (preset) => {
    if (preset.days === null) {
      setAdvDraft((d) => ({ ...d, date_from: '', date_to: '' }));
      return;
    }
    if (preset.days === 0) {
      const t = todayYmd();
      setAdvDraft((d) => ({ ...d, date_from: t, date_to: t }));
      return;
    }
    setAdvDraft((d) => ({
      ...d,
      date_from: daysAgoYmd(preset.days),
      date_to: todayYmd()
    }));
  };

  const selectMed = (med) => {
    setAdvDraft((d) => ({ ...d, medication_id: String(med.id) }));
    setMedQuery(med.name || '');
  };

  const clearMed = () => {
    setAdvDraft((d) => ({ ...d, medication_id: '' }));
    setMedQuery('');
  };

  return (
    <div className="cp-movements-page">
      <PharmacyPageHeader badge={copy.BADGE} title={copy.TITLE} description={copy.DESC} />

      <section className="cp-catalog-stats cp-catalog-stats--5" aria-label="Synthèse mouvements">
        <article className="cp-catalog-stat">
          <span className="cp-catalog-stat__label">{copy.STAT_TOTAL}</span>
          <span className="cp-catalog-stat__value">{loading ? '—' : formatNumber(stats.total)}</span>
        </article>
        <article
          className={`cp-catalog-stat cp-catalog-stat--ok cp-catalog-stat--clickable${
            typeFilter === 'IN' ? ' is-active' : ''
          }`}
          role="button"
          tabIndex={0}
          onClick={() => setTypeFilterSafe((t) => (t === 'IN' ? '' : 'IN'))}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setTypeFilterSafe((t) => (t === 'IN' ? '' : 'IN'));
            }
          }}
        >
          <span className="cp-catalog-stat__label">{copy.STAT_IN}</span>
          <span className="cp-catalog-stat__value">{loading ? '—' : formatNumber(stats.in)}</span>
          <span className="cp-catalog-stat__hint">
            {loading ? '' : `+${formatNumber(stats.qtyIn)} u.`}
          </span>
        </article>
        <article
          className={`cp-catalog-stat cp-catalog-stat--amber cp-catalog-stat--clickable${
            typeFilter === 'OUT' ? ' is-active' : ''
          }`}
          role="button"
          tabIndex={0}
          onClick={() => setTypeFilterSafe((t) => (t === 'OUT' ? '' : 'OUT'))}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setTypeFilterSafe((t) => (t === 'OUT' ? '' : 'OUT'));
            }
          }}
        >
          <span className="cp-catalog-stat__label">{copy.STAT_OUT}</span>
          <span className="cp-catalog-stat__value">{loading ? '—' : formatNumber(stats.out)}</span>
          <span className="cp-catalog-stat__hint">
            {loading ? '' : `−${formatNumber(stats.qtyOut)} u.`}
          </span>
        </article>
        <article className="cp-catalog-stat cp-catalog-stat--muted">
          <span className="cp-catalog-stat__label">{copy.STAT_TRANSFER}</span>
          <span className="cp-catalog-stat__value">{loading ? '—' : formatNumber(stats.transfer)}</span>
        </article>
        <article
          className={`cp-catalog-stat cp-catalog-stat--clickable${
            typeFilter === 'DISPENSE' ? ' is-active' : ''
          }`}
          role="button"
          tabIndex={0}
          onClick={() => setTypeFilterSafe((t) => (t === 'DISPENSE' ? '' : 'DISPENSE'))}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setTypeFilterSafe((t) => (t === 'DISPENSE' ? '' : 'DISPENSE'));
            }
          }}
        >
          <span className="cp-catalog-stat__label">{copy.STAT_DISPENSE}</span>
          <span className="cp-catalog-stat__value">
            {loading ? '—' : formatNumber(stats.dispense)}
          </span>
        </article>
      </section>

      <div className="cp-card">
        <div className="cp-catalog-toolbar cp-catalog-toolbar--stack">
          <div className="cp-search-wrap">
            <Icon name="search" size={16} className="cp-search-wrap__icon" aria-hidden />
            <input
              type="search"
              className="cp-search-wrap__input"
              placeholder={copy.SEARCH_PLACEHOLDER}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              aria-label={copy.SEARCH_ARIA}
            />
          </div>
          <div className="cp-catalog-toolbar__end cp-catalog-toolbar__end--wrap">
            <button
              type="button"
              className={`cp-btn cp-btn--ghost cp-btn--sm${advOpen ? ' is-active' : ''}${
                activeAdvCount ? ' cp-btn--filter-active' : ''
              }`}
              onClick={() => {
                setAdvDraft({ ...advApplied });
                setMedQuery(selectedMedName);
                setAdvOpen((o) => !o);
              }}
              aria-expanded={advOpen}
              aria-label={copy.ADV_TOGGLE_ARIA}
            >
              <Icon name="filter" size={14} aria-hidden />
              {copy.ADV_TOGGLE}
              {activeAdvCount ? (
                <span className="cp-filter-count">{activeAdvCount}</span>
              ) : null}
            </button>
            <div className="cp-catalog-meta">
              <strong>{formatNumber(total)}</strong>
              <span>{copy.RESULTS_LABEL}</span>
            </div>
            <button
              type="button"
              className="cp-icon-btn"
              onClick={load}
              disabled={loading}
              aria-label={copy.REFRESH_ARIA}
              title={copy.REFRESH_ARIA}
            >
              <Icon name="refresh" size={16} />
            </button>
          </div>
        </div>

        {advOpen ? (
          <div className="cp-adv-filters">
            <div className="cp-adv-filters__head">
              <span className="cp-adv-filters__eyebrow">{copy.ADV_EYEBROW}</span>
              <p className="cp-adv-filters__hint">{copy.ADV_HINT}</p>
            </div>

            <div className="cp-adv-filters__presets" role="group" aria-label={copy.ADV_PERIOD}>
              {PERIOD_PRESETS.map((p) => {
                const presetLabels = {
                  today: copy.PRESET_TODAY,
                  '7d': copy.PRESET_7D,
                  '30d': copy.PRESET_30D,
                  '90d': copy.PRESET_90D,
                  all: copy.PRESET_ALL
                };
                return (
                  <button
                    key={p.id}
                    type="button"
                    className={`cp-filter-pill${periodPresetId === p.id ? ' is-active' : ''}`}
                    onClick={() => applyPreset(p)}
                  >
                    {presetLabels[p.id]}
                  </button>
                );
              })}
            </div>

            <div className="cp-adv-filters__grid">
              <label className="cp-adv-filters__field">
                <span>{copy.ADV_FROM}</span>
                <Form.Control
                  type="date"
                  size="sm"
                  value={advDraft.date_from || ''}
                  onChange={(e) => setAdvDraft((d) => ({ ...d, date_from: e.target.value }))}
                />
              </label>
              <label className="cp-adv-filters__field">
                <span>{copy.ADV_TO}</span>
                <Form.Control
                  type="date"
                  size="sm"
                  value={advDraft.date_to || ''}
                  onChange={(e) => setAdvDraft((d) => ({ ...d, date_to: e.target.value }))}
                />
              </label>
              {!siteOnly ? (
                <label className="cp-adv-filters__field">
                  <span>{copy.ADV_SITE}</span>
                  <Form.Select
                    size="sm"
                    value={advDraft.site_id}
                    onChange={(e) => setAdvDraft((d) => ({ ...d, site_id: e.target.value }))}
                  >
                    <option value="">{copy.ADV_SITE_ALL}</option>
                    {workSites.map((s) => (
                      <option key={s.id} value={String(s.id)}>
                        {s.name}
                      </option>
                    ))}
                  </Form.Select>
                </label>
              ) : null}
              <label className="cp-adv-filters__field">
                <span>{copy.ADV_REF}</span>
                <Form.Select
                  size="sm"
                  value={advDraft.reference_type}
                  onChange={(e) => setAdvDraft((d) => ({ ...d, reference_type: e.target.value }))}
                >
                  {REF_KEYS.map((r) => (
                    <option key={r || 'all'} value={r}>
                      {refLabel(r)}
                    </option>
                  ))}
                </Form.Select>
              </label>
              <div className="cp-adv-filters__field cp-adv-filters__field--med">
                <span>{copy.ADV_MED}</span>
                <div className="cp-adv-med">
                  <input
                    type="search"
                    className="form-control form-control-sm"
                    placeholder={copy.ADV_MED_SEARCH}
                    value={medQuery}
                    onChange={(e) => {
                      setMedQuery(e.target.value);
                      if (!e.target.value) clearMed();
                    }}
                    aria-label={copy.ADV_MED}
                  />
                  {advDraft.medication_id ? (
                    <button
                      type="button"
                      className="cp-adv-med__clear"
                      onClick={clearMed}
                      aria-label="Effacer le médicament"
                    >
                      <Icon name="times" size={12} />
                    </button>
                  ) : null}
                  {medSuggestions.length > 0 && !advDraft.medication_id ? (
                    <ul className="cp-adv-med__list">
                      {medSuggestions.map((m) => (
                        <li key={m.id}>
                          <button type="button" onClick={() => selectMed(m)}>
                            {m.name}
                          </button>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </div>
              </div>
            </div>

            <div className="cp-adv-filters__actions">
              <button type="button" className="cp-btn cp-btn--ghost cp-btn--sm" onClick={resetAdvanced}>
                {copy.ADV_RESET}
              </button>
              <button type="button" className="cp-btn cp-btn--primary cp-btn--sm" onClick={applyAdvanced}>
                {copy.ADV_APPLY}
              </button>
            </div>
          </div>
        ) : null}

        {activeAdvCount > 0 && !advOpen ? (
          <div className="cp-active-filters">
            <span className="cp-active-filters__label">
              {copy.ACTIVE_FILTERS.replace('{n}', String(activeAdvCount))}
            </span>
            {advApplied.date_from || advApplied.date_to ? (
              <span className="cp-active-filters__chip">
                {advApplied.date_from || '…'} → {advApplied.date_to || '…'}
              </span>
            ) : null}
            {advApplied.site_id && !siteOnly ? (
              <span className="cp-active-filters__chip">
                {workSites.find((s) => String(s.id) === String(advApplied.site_id))?.name ||
                  `Site #${advApplied.site_id}`}
              </span>
            ) : null}
            {advApplied.reference_type ? (
              <span className="cp-active-filters__chip">{refLabel(advApplied.reference_type)}</span>
            ) : null}
            {selectedMedName ? (
              <span className="cp-active-filters__chip">{selectedMedName}</span>
            ) : null}
            <button type="button" className="cp-active-filters__clear" onClick={resetAdvanced}>
              {copy.ADV_RESET}
            </button>
          </div>
        ) : null}

        <div className="cp-movements-typebar" role="group" aria-label={copy.FILTER_TYPE_ARIA}>
          {TYPE_KEYS.map((t) => {
            const count = t ? typeCount(t) : stats.total;
            return (
              <button
                key={t || 'all'}
                type="button"
                className={`cp-filter-pill${typeFilter === t ? ' is-active' : ''}`}
                onClick={() => setTypeFilterSafe(t)}
              >
                {typeLabel(t)}
                <span className="cp-filter-pill__count">{formatNumber(count)}</span>
              </button>
            );
          })}
        </div>

        <div className="cp-card__body cp-card__body--flush">
          {loading ? (
            <LoadingBlock />
          ) : rows.length === 0 ? (
            <div className="cp-empty-state">
              <span className="cp-empty-state__icon" aria-hidden>
                <Icon name="history" size={28} />
              </span>
              <h3 className="cp-empty-state__title">{copy.EMPTY}</h3>
              <p className="cp-empty-state__text mb-0">{copy.EMPTY_HINT}</p>
            </div>
          ) : (
            <>
              <div className="cp-table-wrap">
                <Table responsive hover className="cp-table cp-movements-table mb-0">
                  <thead>
                    <tr>
                      <th>{copy.COL_DATE}</th>
                      <th>{copy.COL_TYPE}</th>
                      <th>{copy.COL_MED}</th>
                      <th>{copy.COL_SITE}</th>
                      <th>{copy.COL_LOT}</th>
                      <th className="text-end">{copy.COL_QTY}</th>
                      <th className="text-end">{copy.COL_STOCK}</th>
                      <th>{copy.COL_ACTOR}</th>
                      <th>{copy.COL_REF}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((m) => {
                      const signed = signedQuantity(m);
                      const qtyClass =
                        signed > 0 ? 'cp-qty cp-qty--in' : signed < 0 ? 'cp-qty cp-qty--out' : 'cp-qty';
                      return (
                        <tr
                          key={m.id}
                          className="cp-table-row--clickable"
                          onClick={() => setDetail(m)}
                        >
                          <td className="cp-num text-muted small">{formatDateTime(m.created_at)}</td>
                          <td>
                            <span className={`cp-status-pill ${typePillClass(m.movement_type)}`}>
                              {typeLabel(m.movement_type)}
                            </span>
                          </td>
                          <td>
                            <strong>{m.medication_name}</strong>
                            {m.unit_name ? (
                              <div className="small text-muted">{m.unit_name}</div>
                            ) : null}
                          </td>
                          <td>{m.site_name}</td>
                          <td className="small">
                            {m.lot_number || '—'}
                            {m.expiry_date ? (
                              <div className="text-muted">{formatDate(m.expiry_date)}</div>
                            ) : null}
                          </td>
                          <td className={`text-end ${qtyClass}`}>
                            {signed > 0 ? '+' : ''}
                            {formatNumber(signed)}
                          </td>
                          <td className="text-end cp-num small text-muted">
                            {m.quantity_before != null && m.quantity_after != null
                              ? `${formatNumber(m.quantity_before)} → ${formatNumber(m.quantity_after)}`
                              : '—'}
                          </td>
                          <td className="small">{actorName(m)}</td>
                          <td className="small text-muted">{refLabel(m.reference_type, m)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>

              {total > 0 ? (
                <div className="cp-pagination">
                  <div className="cp-pagination__meta">
                    {copy.PAGE_RANGE.replace('{from}', String(rangeFrom))
                      .replace('{to}', String(rangeTo))
                      .replace('{total}', formatNumber(total))}
                  </div>

                  <div className="cp-pagination__controls">
                    <label className="cp-pagination__page-size">
                      <span>{copy.PAGE_SIZE}</span>
                      <Form.Select
                        size="sm"
                        value={String(pageSize)}
                        onChange={(e) => changePageSize(Number(e.target.value))}
                        aria-label={copy.PAGE_SIZE_ARIA}
                        disabled={loading}
                      >
                        {PAGE_SIZES.map((n) => (
                          <option key={n} value={n}>
                            {n}
                          </option>
                        ))}
                      </Form.Select>
                    </label>

                    <nav className="cp-pagination__nav" aria-label="Pagination des mouvements">
                      <button
                        type="button"
                        className="cp-btn cp-btn--ghost cp-btn--sm cp-pagination__edge"
                        disabled={page <= 1 || loading}
                        onClick={() => setPage(1)}
                        aria-label={copy.FIRST}
                        title={copy.FIRST}
                      >
                        «
                      </button>
                      <button
                        type="button"
                        className="cp-btn cp-btn--ghost cp-btn--sm"
                        disabled={page <= 1 || loading}
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                        aria-label={copy.PREV}
                      >
                        <Icon name="chevron-left" size={14} aria-hidden />
                        {copy.PREV}
                      </button>

                      <div className="cp-pagination__pages">
                        {pageItems.map((n, idx) => {
                          const prev = pageItems[idx - 1];
                          const showEllipsis = prev != null && n - prev > 1;
                          return (
                            <React.Fragment key={n}>
                              {showEllipsis ? (
                                <span className="cp-pagination__ellipsis" aria-hidden>
                                  …
                                </span>
                              ) : null}
                              <button
                                type="button"
                                className={`cp-pagination__page${page === n ? ' is-active' : ''}`}
                                disabled={loading}
                                onClick={() => setPage(n)}
                                aria-current={page === n ? 'page' : undefined}
                              >
                                {n}
                              </button>
                            </React.Fragment>
                          );
                        })}
                      </div>

                      <button
                        type="button"
                        className="cp-btn cp-btn--ghost cp-btn--sm"
                        disabled={page >= pageCount || loading}
                        onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                        aria-label={copy.NEXT}
                      >
                        {copy.NEXT}
                        <Icon name="chevron-right" size={14} aria-hidden />
                      </button>
                      <button
                        type="button"
                        className="cp-btn cp-btn--ghost cp-btn--sm cp-pagination__edge"
                        disabled={page >= pageCount || loading}
                        onClick={() => setPage(pageCount)}
                        aria-label={copy.LAST}
                        title={copy.LAST}
                      >
                        »
                      </button>
                    </nav>
                  </div>
                </div>
              ) : null}
            </>
          )}
        </div>
      </div>

      <Modal show={Boolean(detail)} onHide={() => setDetail(null)} centered className="cp-modal">
        <Modal.Header closeButton>
          <Modal.Title>{copy.DETAIL_TITLE}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {detail ? (
            <div className="cp-movement-detail">
              <div className="cp-movement-detail__hero">
                <span className={`cp-status-pill ${typePillClass(detail.movement_type)}`}>
                  {typeLabel(detail.movement_type)}
                </span>
                <strong className="cp-movement-detail__med">{detail.medication_name}</strong>
                <span className="text-muted small">{formatDateTime(detail.created_at)}</span>
              </div>
              <dl className="cp-movement-detail__grid">
                <div>
                  <dt>{copy.COL_SITE}</dt>
                  <dd>{detail.site_name}</dd>
                </div>
                <div>
                  <dt>{copy.COL_QTY}</dt>
                  <dd
                    className={
                      signedQuantity(detail) >= 0 ? 'cp-qty cp-qty--in' : 'cp-qty cp-qty--out'
                    }
                  >
                    {signedQuantity(detail) > 0 ? '+' : ''}
                    {formatNumber(signedQuantity(detail))}
                    {detail.unit_name ? ` ${detail.unit_name}` : ''}
                  </dd>
                </div>
                <div>
                  <dt>{copy.DETAIL_BEFORE}</dt>
                  <dd>
                    {detail.quantity_before != null ? formatNumber(detail.quantity_before) : '—'}
                  </dd>
                </div>
                <div>
                  <dt>{copy.DETAIL_AFTER}</dt>
                  <dd>
                    {detail.quantity_after != null ? formatNumber(detail.quantity_after) : '—'}
                  </dd>
                </div>
                <div>
                  <dt>{copy.COL_LOT}</dt>
                  <dd>{detail.lot_number || '—'}</dd>
                </div>
                <div>
                  <dt>{copy.DETAIL_EXPIRY}</dt>
                  <dd>{detail.expiry_date ? formatDate(detail.expiry_date) : '—'}</dd>
                </div>
                <div>
                  <dt>{copy.COL_REF}</dt>
                  <dd>{refLabel(detail.reference_type, detail)}</dd>
                </div>
                <div>
                  <dt>{copy.DETAIL_REF_ID}</dt>
                  <dd>{detail.reference_id != null ? `#${detail.reference_id}` : '—'}</dd>
                </div>
                <div>
                  <dt>{copy.COL_ACTOR}</dt>
                  <dd>{actorName(detail)}</dd>
                </div>
                <div className="cp-movement-detail__reason">
                  <dt>{copy.COL_REASON}</dt>
                  <dd>{detail.reason || '—'}</dd>
                </div>
              </dl>
            </div>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="cp-btn cp-btn--ghost" onClick={() => setDetail(null)}>
            {copy.DETAIL_CLOSE}
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
